import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { userRoles } from '../../../../environments/environment';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { AddCategoryComponent } from '../../../shared/components/dialog/add-category/add-category.component';
import { AlertDialogComponent } from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';

// 0 = Student, 1 = Teacher, 2 = CSQ
type FAQAudienceType = 0 | 1 | 2;

interface FAQ {
    id: number;
    question: string;
    answer: string;
}

interface Category {
    id: string;
    name: string;
    faqs: FAQ[];
    isOpen: boolean;
    isActive: boolean;
}

interface ApiDataResponse {
    status: boolean;
    message: string;
    data: any;
}

interface FAQCategoryResponse {
    faqCategoryId: string;
    categoryName: string;
    isActive: boolean;
}

interface FAQSection {
    label: string;
    audienceType: FAQAudienceType;
}

@Component({
    selector: 'app-faq-categories',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, FormsModule, ButtonComponent],
    templateUrl: './faq-categories.component.html',
    styleUrls: ['./faq-categories.component.scss'],
    animations: [
        trigger('slideInOut', [
            state('void', style({ height: '0', opacity: 0 })),
            state('*', style({ height: '*', opacity: 1 })),
            transition(':enter', [animate('300ms ease-out')]),
            transition(':leave', [animate('300ms ease-in')])
        ])
    ]
})
export class FAQCategoriesComponent implements OnInit {
    search: string = '';
    categories: Category[] = [];
    filteredCategories: Category[] = [];
    isLoading: boolean = false;

    activeSection: FAQAudienceType = 0;

    readonly sections: FAQSection[] = [
        { label: 'Student FAQ', audienceType: 0 },
        { label: 'Teacher FAQ', audienceType: 1 },
        { label: 'CSQ FAQ',     audienceType: 2 },
    ];

    userRoles = {
        addNew: false,
        listItem: { edit: false, delete: false }
    };

    constructor(
        public router: Router,
        private dialog: MatDialog,
        private httpService: HttpService,
        private location: Location,
        private snackBar: MatSnackBar,
    ) { }

    ngOnInit(): void {
        this.fetchFAQCategoryList(this.activeSection).subscribe();

        const user = localStorage.getItem('userRole');
        if (user && user in userRoles) {
            this.userRoles = userRoles[user as keyof typeof userRoles]?.faqCategories;
        }
    }

    setSection(section: FAQAudienceType): void {
        if (this.activeSection === section) return;
        this.activeSection = section;
        this.search = '';
        this.categories = [];
        this.filteredCategories = [];
        this.fetchFAQCategoryList(section).subscribe();
    }

    updateSearch(searchValue: string): void {
        this.search = searchValue;
        this.applyFilter();
    }

    applyFilter(): void {
        if (!this.search) {
            this.filteredCategories = [...this.categories];
        } else {
            const searchTerm = this.search.toLowerCase();
            this.filteredCategories = this.categories.filter(category =>
                category.name.toLowerCase().includes(searchTerm)
            );
        }
    }

    goBack(): void {
        this.location.back();
    }

    openAddCategory(): void {
        const dialogRef = this.dialog.open(AddCategoryComponent, {
            width: '34.375rem',
            data: { initialData: undefined, EditMode: false, type: false, audienceType: this.activeSection },
            disableClose: true,
            autoFocus: false,
            panelClass: 'custom-dialog-container'
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.success) {
                this.fetchFAQCategoryList(this.activeSection).subscribe();
                this.showSuccessToast('Category added successfully!');
            }
        });
    }

    toggleCategory(index: number): void {
        this.filteredCategories[index].isOpen = !this.filteredCategories[index].isOpen;
    }

    editCategory(category: Category): void {
        const dialogRef = this.dialog.open(AddCategoryComponent, {
            width: '34.375rem',
            data: { initialData: category, EditMode: true, type: false, audienceType: this.activeSection },
            disableClose: true,
            autoFocus: false,
            panelClass: 'custom-dialog-container'
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result?.success) {
                this.fetchFAQCategoryList(this.activeSection).subscribe();
            }
        });
    }

    deleteCategory(categoryId: string): void {
        const dialogData = {
            title: 'Confirm delete',
            message: `Are you sure you want to delete this FAQ category?`,
            submessage: 'This category contains existing FAQs. Deleting it will also mark all related FAQs as inactive.',
            okText: 'Delete',
            cancelText: 'Cancel',
            icon: 'warning',
        };

        this.dialog.open(AlertDialogComponent, { width: '30.438rem', data: dialogData })
            .afterClosed().subscribe(result => {
                if (result) {
                    this.performDeleteCategory(categoryId);
                }
            });
    }

    private performDeleteCategory(categoryId: string): void {
        this.httpService.delete<ApiDataResponse>(`faqcategories/${categoryId}`).pipe(
            tap({
                next: (response) => {
                    if (response.status) {
                        this.showSuccessToast(response?.message || 'Category deleted successfully!');
                        this.fetchFAQCategoryList(this.activeSection).subscribe();
                    } else {
                        this.showErrorToast(response?.message || 'Something went wrong. Please try again later.');
                    }
                },
                error: (err) => {
                    this.showErrorToast(err?.message || 'Something went wrong. Please try again later.');
                }
            })
        ).subscribe();
    }

    fetchFAQCategoryList(section: FAQAudienceType = this.activeSection) {
        this.isLoading = true;
        return this.httpService.get<ApiDataResponse>(`faqcategories?audienceType=${section}`).pipe(
            tap({
                next: (response) => {
                    if (response.status && response.data) {
                        this.categories = this.transformApiData(response.data);
                        this.filteredCategories = [...this.categories];
                    } else {
                        this.categories = [];
                        this.filteredCategories = [];
                    }
                    this.isLoading = false;
                },
                error: (err) => {
                    this.showErrorToast(err?.message);
                    this.isLoading = false;
                    this.categories = [];
                    this.filteredCategories = [];
                }
            })
        );
    }

    private transformApiData(apiData: FAQCategoryResponse[]): Category[] {
        return apiData.map(item => ({
            id: item.faqCategoryId,
            name: item.categoryName,
            isActive: item.isActive,
            isOpen: false,
            faqs: []
        }));
    }

    private showSuccessToast(message: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast']
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }
}
